<template>
  <v-dialog v-model="show" max-width="950px" persistent v-if="localSite">
    <v-card>
      <v-card-title>
        <div>
          <h3>
            {{ site ? $t("Generic.Edit") : $t("Generic.Create") }}
            {{
              localSite.name == "" ? $t("Manage_Sites.Site") : localSite.name
            }}
          </h3>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon v-on:click.native="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider class="mx-3"></v-divider>
      <v-card-text>
        <v-container grid-list-md>
          <v-row>
            <v-col cols="12">
              <v-form ref="form" v-model="valid">
                <v-text-field
                  :label="$t('Generic.Name')"
                  v-model="localSite.name"
                  :rules="[v => !!v || $t('Rules.Name_required')]"
                ></v-text-field>

                <v-autocomplete
                  v-model="localSite.installerId"
                  :items="installers"
                  :label="$t('Manage_Sites.Installer')"
                  item-text="name"
                  item-value="id"
                >
                </v-autocomplete>

                <v-divider v-if="site" class="my-2"></v-divider>

                <v-btn
                  v-if="site && !localSite.publicAccess"
                  v-on:click="loadPublicAccess"
                >
                  {{ $t("Manage_Sites.Public_Access") }}
                </v-btn>

                <div
                  v-if="site && localSite.publicAccess"
                  class="public-access-container"
                >
                  <span class="public-access-text">
                    {{ formattedPublicAccess }}
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn outlined v-on:click="copyPublicAccess">
                    <v-icon left>
                      mdi-content-copy
                    </v-icon>
                    {{ $t("Generic.Copy") }}
                  </v-btn>
                </div>

                <v-divider class="my-2"></v-divider>

                <span>{{ $t("Manage_Sites.SolarEdge_Providers") }}</span>

                <site-providers-table
                  v-if="!!localSite.solarEdgeProviders"
                  class="mb-3"
                  :siteName="localSite.name"
                  :providers="localSite.solarEdgeProviders"
                  v-on:provider-api-key-fetched="onSolarEdgeApiKeyFetched"
                ></site-providers-table>

                <v-divider class="my-2"></v-divider>

                <span>{{ $t("Manage_Sites.Solplanet_Providers") }}</span>

                <site-solplanet-providers-table
                  v-if="!!localSite.solplanetProviders"
                  class="mb-3"
                  :providers="localSite.solplanetProviders"
                  v-on:provider-api-key-fetched="onSolplanetApiKeyFetched"
                ></site-solplanet-providers-table>

                <div v-if="!!selectedInstaller">
                  <v-divider class="my-2"></v-divider>

                  <span class="subtitle-1">{{
                    $t("Manage_Sites.FusionSolar_Providers")
                  }}</span>

                  <site-fusion-solar-providers-table
                    class="mb-3"
                    :providers="localSite.fusionSolarProviders"
                    :accesses="selectedInstaller.fusionSolarAccesses"
                  ></site-fusion-solar-providers-table>
                </div>
              </v-form>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text v-on:click="show = false">{{ $t("Generic.Cancel") }}</v-btn>
        <v-btn
          text
          :color="site ? 'blue' : 'green'"
          v-on:click="validateSite"
          :disabled="!valid"
          >{{
            site
              ? $t("Generic.Save")
              : $t("Generic.Create") + " " + localSite.name
          }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import SiteService from "@/services/SiteService";
import SiteProvidersTable from "./SiteProvidersTable.vue";
import SiteSolplanetProvidersTable from "./SiteSolplanetProvidersTable.vue";
import SiteFusionSolarProvidersTable from "./SiteFusionSolarProvidersTable.vue";
import InstallerService from "../../services/InstallerService";

export default {
  components: {
    SiteProvidersTable,
    SiteSolplanetProvidersTable,
    SiteFusionSolarProvidersTable
  },
  props: {
    value: Boolean,
    site: Object
  },
  data() {
    return {
      localSite: undefined,
      initialProviders: {
        solarEdgeProviders: [],
        solplanetProviders: [],
        fusionSolarProviders: []
      },
      valid: false,
      installers: []
    };
  },
  computed: {
    selectedInstaller() {
      return this.localSite?.installerId
        ? this.installers.find(item => item.id === this.localSite.installerId)
        : undefined;
    },
    formattedPublicAccess() {
      return this.formatPublicAccess(this.localSite.publicAccess);
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  mounted() {
    InstallerService.GetAllInstallers(true)
      .then(response => (this.installers = response.data))
      .catch(() =>
        this.$emit("show-error", this.$i18n.t("Error.Installers_Not_Retrieved"))
      );
  },
  methods: {
    splitProvidersBySupplierType(providers) {
      const splitProviders = {
        solarEdgeProviders: [],
        solplanetProviders: [],
        fusionSolarProviders: []
      };

      if (!Array.isArray(providers)) {
        return splitProviders;
      }

      for (const provider of providers) {
        if (provider.supplierType === "SolarEdge") {
          splitProviders.solarEdgeProviders.push(provider);
        } else if (provider.supplierType === "Solplanet") {
          splitProviders.solplanetProviders.push(provider);
        } else if (provider.supplierType === "FusionSolar") {
          splitProviders.fusionSolarProviders.push(provider);
        }
      }

      return splitProviders;
    },
    validateSite() {
      if (this.$refs.form.validate()) {
        const resultSite = {
          id: this.localSite.id,
          installerId: this.localSite.installerId,
          name: this.localSite.name,
          solarEdgeProviderChanges: this.getSolarEdgeProviderChanges(),
          solplanetProviderChanges: this.getSolplanetProviderChanges(),
          fusionSolarProviderChanges: this.getFusionSolarProviderChanges()
        };

        this.$emit(
          this.site ? "validate-edit-site" : "validate-create-site",
          resultSite
        );
      }
    },
    getSolarEdgeProviderChanges() {
      let changes = {
        added: [],
        modified: [],
        deleted: []
      };

      for (const provider of this.localSite.solarEdgeProviders) {
        if (!provider.id) {
          changes.added.push(provider);
        } else {
          const initialProvider = this.initialProviders.solarEdgeProviders.find(
            ip => ip.id === provider.id
          );

          if (
            initialProvider.name !== provider.name ||
            initialProvider.apiSiteId !== provider.apiSiteId ||
            initialProvider.apiKey !== provider.apiKey
          ) {
            changes.modified.push(provider);
          }
        }
      }

      for (const ip of this.initialProviders.solarEdgeProviders) {
        const finalProvider = this.localSite.solarEdgeProviders.find(
          provider => provider.id === ip.id
        );

        if (!finalProvider) {
          changes.deleted.push(ip);
        }
      }

      return changes;
    },
    getSolplanetProviderChanges() {
      let changes = {
        added: [],
        modified: [],
        deleted: []
      };

      for (const provider of this.localSite.solplanetProviders) {
        if (!provider.id) {
          changes.added.push(provider);
        } else {
          const initialProvider = this.initialProviders.solplanetProviders.find(
            ip => ip.id === provider.id
          );

          if (
            initialProvider.name !== provider.name ||
            initialProvider.apiKey !== provider.apiKey
          ) {
            changes.modified.push(provider);
          }
        }
      }

      for (const ip of this.initialProviders.solplanetProviders) {
        const finalProvider = this.localSite.solplanetProviders.find(
          provider => provider.id === ip.id
        );

        if (!finalProvider) {
          changes.deleted.push(ip);
        }
      }

      return changes;
    },
    getFusionSolarProviderChanges() {
      let changes = {
        added: [],
        modified: [],
        deleted: []
      };

      for (const provider of this.localSite.fusionSolarProviders) {
        if (!provider.id) {
          changes.added.push(provider);
        } else {
          const initialProvider = this.initialProviders.fusionSolarProviders.find(
            ip => ip.id === provider.id
          );

          if (
            initialProvider.name !== provider.name ||
            initialProvider.stationId !== provider.stationId ||
            initialProvider.inverterId !== provider.inverterId ||
            initialProvider.accessId !== provider.accessId
          ) {
            changes.modified.push(provider);
          }
        }
      }

      for (const ip of this.initialProviders.fusionSolarProviders) {
        const finalProvider = this.localSite.fusionSolarProviders.find(
          provider => provider.id === ip.id
        );

        if (!finalProvider) {
          changes.deleted.push(ip);
        }
      }

      return changes;
    },
    loadPublicAccess() {
      SiteService.GetPublicAccess(this.site.id)
        .then(response => {
          this.localSite.publicAccess = response.data.key;
        })
        .catch(error =>
          this.$emit("show-error", this.$i18n.t("Error.Sites_Not_Retrieved"))
        );
    },
    copyPublicAccess() {
      navigator.clipboard.writeText(this.localSite.publicAccess);
    },
    onSolarEdgeApiKeyFetched(payload) {
      const initialProvider = this.initialProviders.solarEdgeProviders.find(
        provider => provider.id === payload.providerId
      );

      if (initialProvider) {
        this.$set(initialProvider, "apiKey", payload.apiKey);
      }
    },
    onSolplanetApiKeyFetched(payload) {
      const initialProvider = this.initialProviders.solplanetProviders.find(
        provider => provider.id === payload.providerId
      );

      if (initialProvider) {
        this.$set(initialProvider, "apiKey", payload.apiKey);
      }
    },
    formatPublicAccess(publicAccess) {
      return publicAccess.match(/.{1,4}/g).join(" ");
    }
  },
  watch: {
    value(val) {
      if (val) {
        if (this.site) {
          SiteService.GetProviders(this.site.id)
            .then(response => {
              const splitProviders = this.splitProvidersBySupplierType(
                response.data
              );

              this.localSite = {
                ...this.site,
                ...splitProviders,
                publicAccess: ""
              };

              this.initialProviders = JSON.parse(
                JSON.stringify(splitProviders)
              );
            })
            .catch(error =>
              this.$emit(
                "show-error",
                this.$i18n.t("Error.Sites_Not_Retrieved")
              )
            );
        } else {
          this.localSite = {
            name: "",
            installerId: null,
            solarEdgeProviders: [],
            solplanetProviders: [],
            fusionSolarProviders: [],
            publicAccess: ""
          };

          this.initialProviders = {
            solarEdgeProviders: [],
            solplanetProviders: [],
            fusionSolarProviders: []
          };
        }
      }
    }
  }
};
</script>
<style scoped>
.public-access-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.public-access-text {
  font-weight: 500;
  letter-spacing: 1px;
  font-size: 1.8rem;
}
</style>
